import React, { useEffect, useState } from "react";
import api, { launchEnrollment } from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { History, ExternalLink, ArrowUpRight, Sparkles, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const REL = { self: "Self", spouse: "Spouse", domestic_partner: "Partner", child: "Child", parent: "Parent", other_dependent: "Dependent" };

export default function HandoffHistory() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null); // {loading, data, name}

  const openSummary = async (e) => {
    setSummary({ loading: true, name: e.consumer_name });
    try {
      const { data } = await api.get(`/advisor/households/${e.household_id}/summary`);
      setSummary({ loading: false, data, name: e.consumer_name });
    } catch (_) {
      setSummary({ loading: false, data: { ready: false }, name: e.consumer_name });
    }
  };

  useEffect(() => {
    api.get("/advisor/handoffs").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-6xl mx-auto animate-rise">
      <PageHeader
        eyebrow="Enrollment activity"
        title="Hand-off history"
        subtitle="A first-class record of every consumer routed to an approved enrollment destination — provider, account, journey, and when."
        action={<DemoBadge className="mt-1" />}
      />

      {loading ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : events.length ? (
        <div className="bg-white rounded-xl border border-mist-200 overflow-hidden">
          <Table data-testid="handoff-table">
            <TableHeader>
              <TableRow>
                <TableHead>Consumer</TableHead>
                <TableHead>Journey</TableHead>
                <TableHead>Provider · Account</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>By</TableHead>
                <TableHead>When</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.id} data-testid={`handoff-${e.id}`}>
                  <TableCell>
                    <p className="font-semibold text-navy-900">{e.consumer_name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{REL[e.subject_relationship] || e.subject_relationship}</p>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{e.state} · {e.product_type}</TableCell>
                  <TableCell className="text-muted-foreground">{e.provider_name} · {e.account_ref || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{e.destination_label}</TableCell>
                  <TableCell className="text-muted-foreground capitalize">{(e.initiated_by_role || "").replace("_", " ")}</TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(e.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {e.household_id && (
                      <Button size="sm" variant="ghost" onClick={() => openSummary(e)} data-testid={`handoff-summary-${e.id}`} className="mr-1">
                        <Sparkles className="w-4 h-4 text-gold-dark mr-1" /> Summary
                      </Button>
                    )}
                    {e.destination_url && (
                      <Button size="icon" variant="ghost" onClick={() => launchEnrollment(e.intake_id, e.destination_url)} data-testid={`handoff-open-${e.id}`}>
                        <ExternalLink className="w-4 h-4 text-navy-700" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-dashed border-mist-300 p-12 text-center" data-testid="no-handoffs">
          <History className="w-10 h-10 text-mist-300 mx-auto mb-3" />
          <p className="font-semibold text-navy-900">No hand-offs yet</p>
          <p className="text-sm text-muted-foreground mt-1">When a consumer is routed to an enrollment destination, it will appear here.</p>
        </div>
      )}

      <Dialog open={!!summary} onOpenChange={(o) => !o && setSummary(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="advisor-summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-gold-dark" /> What needs attention {summary?.name ? `— ${summary.name}` : ""}</DialogTitle>
            <DialogDescription>Deterministic review findings for this household, with an optional plain-English AI summary.</DialogDescription>
          </DialogHeader>
          {summary?.loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy-700" /></div>
          ) : !summary?.data?.ready ? (
            <p className="text-sm text-muted-foreground py-6" data-testid="advisor-summary-empty">No completed assessment for this household yet.</p>
          ) : (
            <div className="space-y-4" data-testid="advisor-summary-body">
              {summary.data.narrative && (
                <div className="rounded-xl border border-mist-200 bg-mist-50 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">AI summary</p>
                  <p className="text-sm text-navy-900 leading-relaxed" data-testid="advisor-summary-narrative">{summary.data.narrative}</p>
                </div>
              )}
              {summary.data.gaps?.length > 0 && (
                <div>
                  <p className="text-sm font-bold text-navy-900 mb-2">Areas worth reviewing</p>
                  <ul className="space-y-1.5">
                    {summary.data.gaps.map((g) => (
                      <li key={g.code} className="text-sm text-muted-foreground"><span className="font-semibold text-navy-900">{g.label}.</span> {g.rationale}</li>
                    ))}
                  </ul>
                </div>
              )}
              {summary.data.next_best_action && (
                <p className="text-sm text-navy-900"><span className="font-bold">Suggested next step:</span> {summary.data.next_best_action.label}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
